import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  CheckoutData,
  Address,
  ShippingMethod,
  PaymentMethod,
  Order
} from '../types';
import {
  useGetShippingMethodsQuery,
  useAddShippingMethodMutation,
  useCreatePaymentCollectionMutation,
  useInitializePaymentSessionMutation,
  useCompleteCartMutation,
  useGetCartQuery,
  useUpdateCartMutation,
  useGetRegionQuery,
} from '../services/api';
import { fetchRuntimeConfig } from '../services/config';

/**
 * Checkout ViewModel - Encapsulates checkout flow logic
 * Follows MVVM pattern where this acts as the ViewModel layer
 */
export const useCheckoutViewModel = () => {
  const { cartId } = useSelector((state: RootState) => state.cart);

  // Get cart data from API
  const {
    data: cartData,
    isLoading: isCartLoading,
    error: cartError,
  } = useGetCartQuery(cartId!, {
    skip: !cartId,
  });

  const cart = cartData?.cart;

  // Load regionId from config for fetching region data
  const [regionId, setRegionId] = useState<string>('');
  useEffect(() => {
    fetchRuntimeConfig()
      .then(config => {
        if (config.medusa.regionId) {
          setRegionId(config.medusa.regionId);
        }
      })
      .catch(error => {
        console.warn('[Checkout] Failed to load runtime config:', error);
      });
  }, []);

  // Fetch region from Medusa API (authoritative source for country codes)
  const { data: regionData } = useGetRegionQuery(regionId, {
    skip: !regionId,
  });

  // Set default country code from region API
  useEffect(() => {
    const countries = regionData?.region?.countries;
    if (countries && countries.length > 0) {
      const defaultCountry = countries[0].iso_2;
      console.log('[Checkout] Setting default country code from region API:', defaultCountry);
      setDefaultCountryCode(defaultCountry);
      setCheckoutData(prev => ({
        ...prev,
        shipping_address: {
          ...prev.shipping_address,
          country_code: prev.shipping_address.country_code || defaultCountry,
        },
        billing_address: {
          ...prev.billing_address,
          country_code: prev.billing_address.country_code || defaultCountry,
        },
      }));
    }
  }, [regionData]);

  // Checkout form data
  const [checkoutData, setCheckoutData] = useState<CheckoutData>({
    email: '',
    shipping_address: {
      first_name: '',
      last_name: '',
      address_1: '',
      address_2: '',
      city: '',
      country_code: '',
      postal_code: '',
      province: '',
      phone: '',
      company: '',
    },
    billing_address: {
      first_name: '',
      last_name: '',
      address_1: '',
      address_2: '',
      city: '',
      country_code: '',
      postal_code: '',
      province: '',
      phone: '',
      company: '',
    },
    same_as_shipping: true,
  });

  // Step management
  const [currentStep, setCurrentStep] = useState<'shipping' | 'payment' | 'review'>('shipping');
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [defaultCountryCode, setDefaultCountryCode] = useState<string>('');

  // API mutations
  const [updateCart] = useUpdateCartMutation();
  const [addShippingMethod] = useAddShippingMethodMutation();
  const [createPaymentCollection] = useCreatePaymentCollectionMutation();
  const [initializePaymentSession] = useInitializePaymentSessionMutation();
  const [completeCart] = useCompleteCartMutation();

  // Get shipping methods
  const {
    data: shippingMethodsData,
    isLoading: isLoadingShippingMethods,
    refetch: refetchShippingMethods,
  } = useGetShippingMethodsQuery(cart?.id || '', {
    skip: !cart?.id,
  });

  const shippingMethods = shippingMethodsData?.shipping_options || [];

  // Validation helpers
  const validateAddress = useCallback((address: Address): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!address.first_name.trim()) {
      errors.first_name = 'First name is required';
    }
    if (!address.last_name.trim()) {
      errors.last_name = 'Last name is required';
    }
    if (!address.address_1.trim()) {
      errors.address_1 = 'Address is required';
    }
    if (!address.city.trim()) {
      errors.city = 'City is required';
    }
    if (!address.postal_code.trim()) {
      errors.postal_code = 'Postal code is required';
    }
    if (!address.country_code.trim()) {
      errors.country_code = 'Country is required';
    }

    return errors;
  }, []);

  const validateEmail = useCallback((email: string): string | null => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      return 'Email is required';
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return null;
  }, []);

  // Form update handlers
  const updateEmail = useCallback((email: string) => {
    setCheckoutData(prev => ({ ...prev, email }));

    // Clear email error if it exists
    if (errors.email) {
      setErrors(prev => {
        const { email: _unused, ...rest } = prev;
        void _unused; // Satisfy linter - variable is intentionally unused
        return rest;
      });
    }
  }, [errors.email]);

  const updateShippingAddress = useCallback((address: Partial<Address>) => {
    setCheckoutData(prev => ({
      ...prev,
      shipping_address: { ...prev.shipping_address, ...address },
      // Update billing address too if same_as_shipping is true
      billing_address: prev.same_as_shipping
        ? { ...prev.billing_address, ...address }
        : prev.billing_address,
    }));
  }, []);

  const updateBillingAddress = useCallback((address: Partial<Address>) => {
    setCheckoutData(prev => ({
      ...prev,
      billing_address: { ...prev.billing_address, ...address },
    }));
  }, []);

  const updateSameAsShipping = useCallback((same: boolean) => {
    setCheckoutData(prev => ({
      ...prev,
      same_as_shipping: same,
      billing_address: same ? { ...prev.shipping_address } : prev.billing_address,
    }));
  }, []);

  const updateShippingMethod = useCallback((method: ShippingMethod) => {
    setCheckoutData(prev => ({
      ...prev,
      shipping_method: method,
    }));
  }, []);

  const updatePaymentMethod = useCallback((method: PaymentMethod) => {
    setCheckoutData(prev => ({
      ...prev,
      payment_method: method,
    }));
  }, []);

  // Step navigation
  const goToNextStep = useCallback(async () => {
    if (currentStep === 'shipping') {
      // Validate shipping information
      const emailError = validateEmail(checkoutData.email);
      const shippingErrors = validateAddress(checkoutData.shipping_address);
      const billingErrors = checkoutData.same_as_shipping
        ? {}
        : validateAddress(checkoutData.billing_address);

      const allErrors = {
        ...(emailError ? { email: emailError } : {}),
        ...Object.fromEntries(
          Object.entries(shippingErrors).map(([key, value]) => [`shipping_${key}`, value])
        ),
        ...Object.fromEntries(
          Object.entries(billingErrors).map(([key, value]) => [`billing_${key}`, value])
        ),
      };

      if (Object.keys(allErrors).length > 0) {
        setErrors(allErrors);
        return;
      }

      // Update cart with email and shipping address before moving to payment step
      if (cart?.id) {
        try {
          await updateCart({
            cartId: cart.id,
            email: checkoutData.email,
            shipping_address: checkoutData.shipping_address,
            billing_address: checkoutData.same_as_shipping
              ? checkoutData.shipping_address
              : checkoutData.billing_address,
          }).unwrap();
          console.log('✅ Cart updated with shipping address');

          // Move to payment step and refetch shipping methods with updated cart
          setCurrentStep('payment');
          refetchShippingMethods();
        } catch (error) {
          console.error('❌ Failed to update cart:', error);
          setErrors({ shipping_address: 'Failed to update cart. Please try again.' });
          return;
        }
      } else {
        setCurrentStep('payment');
      }
    } else if (currentStep === 'payment') {
      if (!checkoutData.shipping_method) {
        setErrors({ shipping_method: 'Please select a shipping method' });
        return;
      }
      if (!checkoutData.payment_method) {
        setErrors({ payment_method: 'Please enter valid payment information' });
        return;
      }
      setCurrentStep('review');
    }

    setErrors({});
  }, [currentStep, checkoutData, validateEmail, validateAddress, cart, updateCart, refetchShippingMethods]);

  const goToPreviousStep = useCallback(() => {
    if (currentStep === 'payment') {
      setCurrentStep('shipping');
    } else if (currentStep === 'review') {
      setCurrentStep('payment');
    }
    setErrors({});
  }, [currentStep]);

  // Calculate totals
  const orderSummary = useMemo(() => {
    if (!cart) return null;

    const subtotal = cart.subtotal;
    const shipping = checkoutData.shipping_method?.price || 0;
    const tax = cart.tax_total;
    const total = subtotal + shipping + tax;

    return {
      subtotal,
      shipping,
      tax,
      total,
      currency: cart.currency_code,
    };
  }, [cart, checkoutData.shipping_method]);

  // Complete order using Medusa 2.x payment flow
  const completeOrder = useCallback(async (): Promise<Order | null> => {
    if (!cart || !checkoutData.payment_method) {
      setErrors({ general: 'Missing required information' });
      return null;
    }

    if (!checkoutData.shipping_method) {
      setErrors({ general: 'Please select a shipping method before completing your order' });
      return null;
    }

    setIsProcessing(true);

    try {
      console.log('[Checkout] Starting Medusa 2.x payment flow...');
      const isStripePayment = checkoutData.payment_method.provider_id.includes('stripe');

      // Step 0: Persist shipping method to cart (backend requires this before completion)
      console.log('[Checkout] Step 0: Persisting shipping method to cart...');
      await addShippingMethod({
        cartId: cart.id,
        optionId: checkoutData.shipping_method.id,
      }).unwrap();
      console.log('[Checkout] Shipping method persisted:', checkoutData.shipping_method.id);

      // Step 1: Create payment collection for the cart
      console.log('[Checkout] Step 1: Creating payment collection...');
      const paymentCollectionResult = await createPaymentCollection({
        cartId: cart.id,
      }).unwrap();

      const paymentCollectionId = paymentCollectionResult.payment_collection.id;
      console.log('[Checkout] Payment collection created:', paymentCollectionId);

      // Build item details for PI enrichment
      const itemDetails = cart.items.map(item => ({
        title: item.product?.title || item.title,
        quantity: item.quantity,
        unitPrice: item.unit_price,
      }));

      // Step 2: Initialize payment session with the selected provider
      console.log('[Checkout] Step 2: Initializing payment session with provider:', checkoutData.payment_method.provider_id);
      const config = await fetchRuntimeConfig();
      const stripeAccountId = config.payment?.stripeAccountId || '';
      const platformFee = config.payment?.platformFee;
      const paymentSessionResult = await initializePaymentSession({
        paymentCollectionId,
        providerId: checkoutData.payment_method.provider_id,
        data: checkoutData.payment_method.data,
        context: {
          email: checkoutData.email,
          ...(stripeAccountId && {
            extra: {
              stripeAccountId,
              ...(platformFee && { platformFee }),
              receiptEmail: checkoutData.email,
              itemDetails,
              storeName: config.displayName || config.storeLabel,
            },
          }),
        },
      }).unwrap();
      console.log('[Checkout] Payment session initialized');

      // Step 3: For Stripe, authorize the payment using Stripe.js
      if (isStripePayment) {
        console.log('[Checkout] Step 3: Authorizing Stripe payment...');

        // Get the client_secret from the payment session
        const paymentSession = paymentSessionResult.payment_collection.payment_sessions?.find(
          (s: { provider_id: string }) => s.provider_id.includes('stripe')
        );

        const clientSecret = paymentSession?.data?.client_secret;

        if (!clientSecret) {
          console.error('[Checkout] Stripe client_secret not found in payment session');
          setErrors({ general: 'Payment setup failed. Please try again.' });
          return null;
        }

        // Get Stripe instance and confirmation token from payment method data
        const stripe = checkoutData.payment_method.data?.stripe;
        const confirmationTokenId = checkoutData.payment_method.data?.confirmationTokenId;

        if (!stripe || !confirmationTokenId) {
          console.error('[Checkout] Stripe instance or confirmation token not available');
          setErrors({ general: 'Payment system not ready. Please try again.' });
          return null;
        }

        // Save cartId to sessionStorage before potential redirect (Klarna/Swish)
        sessionStorage.setItem('checkout_cart_id', cart.id);

        // Confirm the payment using the confirmation token created during the payment step.
        // This doesn't require the PaymentElement to be mounted — the token holds
        // all collected payment details (card, Klarna, Swish, etc.).
        const { error: stripeError } = await stripe.confirmPayment({
          clientSecret,
          confirmParams: {
            confirmation_token: confirmationTokenId,
            return_url: `${window.location.origin}/checkout/confirm`,
            receipt_email: checkoutData.email,
          },
          redirect: 'if_required',
        });

        // For redirect methods (Klarna, Swish): browser navigates away, this line never reached
        // For card: continues here
        if (stripeError) {
          console.error('[Checkout] Stripe payment failed:', stripeError);
          sessionStorage.removeItem('checkout_cart_id');
          const stripeErrorMessage = typeof stripeError.message === 'string'
            ? stripeError.message
            : (stripeError.code ? `Payment error: ${stripeError.code}` : 'Payment failed. Please try again.');
          setErrors({ general: stripeErrorMessage });
          return null;
        }

        // Card payment confirmed — clean up sessionStorage
        sessionStorage.removeItem('checkout_cart_id');
        console.log('[Checkout] Stripe payment authorized');
      }

      // Step 4: Complete the cart
      console.log('[Checkout] Step 4: Completing cart...');
      const result = await completeCart({
        cartId: cart.id,
      }).unwrap();

      console.log('[Checkout] Cart completion result:', result);

      // Check the result type
      if (result.type === 'order' && result.order) {
        console.log('[Checkout] Order created successfully:', result.order.id);
        return result.order;
      } else if (result.type === 'cart') {
        // Cart completion failed - might need additional action
        console.error('[Checkout] Cart completion returned cart instead of order:', result.error);
        const cartErrorMessage = typeof result.error === 'string'
          ? result.error
          : 'Payment requires additional action. Please try again.';
        setErrors({ general: cartErrorMessage });
        return null;
      }

      return null;
    } catch (error: unknown) {
      console.error('[Checkout] Order completion failed:', error);
      // Ensure error message is always a string (React error #31 occurs if we pass an object)
      let errorMessage = 'Failed to complete order. Please try again.';
      const err = error as Record<string, unknown>;

      // Handle various error structures:
      // 1. RTK Query error: error.data.message
      // 2. Standard Error: error.message (string)
      // 3. Stripe-style error: { message, name, type } where message might be object
      // 4. Nested error: error.error.message
      const errData = err.data as Record<string, unknown> | undefined;
      const errError = err.error as Record<string, unknown> | undefined;
      if (typeof errData?.message === 'string') {
        errorMessage = errData.message;
      } else if (typeof errError?.message === 'string') {
        // Nested error structure
        errorMessage = errError.message;
      } else if (typeof err.message === 'string') {
        errorMessage = err.message;
      } else if (errData?.message && typeof errData.message === 'object') {
        // error.data.message is an object - stringify it
        const msgObj = errData.message as Record<string, unknown>;
        errorMessage = (msgObj.message as string) || JSON.stringify(errData.message);
      } else if (err.message && typeof err.message === 'object') {
        // error.message is itself an object (unusual but possible)
        const msgObj = err.message as Record<string, unknown>;
        errorMessage = (msgObj.message as string) || JSON.stringify(err.message);
      } else if (typeof error === 'object' && error !== null) {
        // Last resort: stringify the whole error if it's an object
        const errStr = err.message || err.error || JSON.stringify(error);
        errorMessage = typeof errStr === 'string' ? errStr : JSON.stringify(errStr);
      }

      setErrors({ general: errorMessage });
      return null;
    } finally {
      setIsProcessing(false);
    }
  }, [cart, checkoutData, addShippingMethod, createPaymentCollection, initializePaymentSession, completeCart]);

  // Reset checkout
  const resetCheckout = useCallback(() => {
    setCheckoutData({
      email: '',
      shipping_address: {
        first_name: '',
        last_name: '',
        address_1: '',
        address_2: '',
        city: '',
        country_code: defaultCountryCode,
        postal_code: '',
        province: '',
        phone: '',
        company: '',
      },
      billing_address: {
        first_name: '',
        last_name: '',
        address_1: '',
        address_2: '',
        city: '',
        country_code: defaultCountryCode,
        postal_code: '',
        province: '',
        phone: '',
        company: '',
      },
      same_as_shipping: true,
    });
    setCurrentStep('shipping');
    setErrors({});
    setIsProcessing(false);
  }, [defaultCountryCode]);

  return {
    // Data
    cart,
    checkoutData,
    currentStep,
    orderSummary,
    shippingMethods,

    // State
    isProcessing,
    isLoadingShippingMethods,
    isCartLoading,
    cartError,
    errors,

    // Actions
    updateEmail,
    updateShippingAddress,
    updateBillingAddress,
    updateSameAsShipping,
    updateShippingMethod,
    updatePaymentMethod,

    // Navigation
    goToNextStep,
    goToPreviousStep,

    // Order completion
    completeOrder,
    resetCheckout,

    // Validation helpers
    validateAddress,
    validateEmail,
  };
};
